// AI-powered translation service using Google Gemini API
// This provides contextual, meaningful translations instead of hardcoded phrases

interface TranslationCache {
  [key: string]: {
    si: string;
    ta: string;
    timestamp: number;
  };
}

interface GeminiResponse {
  candidates: Array<{
    content: {
      parts: Array<{
        text: string;
      }>;
    };
  }>;
}

export class AITranslationService {
  private static instance: AITranslationService;
  private cache: TranslationCache = {};
  private cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
  private apiKey: string | null = null;
  private isEnabled = false;

  static getInstance(): AITranslationService {
    if (!AITranslationService.instance) {
      AITranslationService.instance = new AITranslationService();
    }
    return AITranslationService.instance;
  }

  constructor() {
    // Get API key from environment variable
    this.apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    this.isEnabled = !!this.apiKey;

    // Load cache from localStorage
    this.loadCache();
  }

  // Load cache from localStorage
  private loadCache() {
    try {
      const cached = localStorage.getItem("ai-translation-cache");
      if (cached) {
        this.cache = JSON.parse(cached);
      }
    } catch (error) {
      console.warn("Failed to load translation cache:", error);
    }
  }

  // Save cache to localStorage
  private saveCache() {
    try {
      localStorage.setItem("ai-translation-cache", JSON.stringify(this.cache));
    } catch (error) {
      console.warn("Failed to save translation cache:", error);
    }
  }

  // Check if translation is cached and still valid
  private getCachedTranslation(
    text: string
  ): { si: string; ta: string } | null {
    const cached = this.cache[text];
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return { si: cached.si, ta: cached.ta };
    }
    return null;
  }

  // Cache translation
  private cacheTranslation(text: string, si: string, ta: string) {
    this.cache[text] = {
      si,
      ta,
      timestamp: Date.now(),
    };
    this.saveCache();
  }

  // Clean old cache entries
  private cleanCache() {
    const now = Date.now();
    Object.keys(this.cache).forEach((key) => {
      if (now - this.cache[key].timestamp > this.cacheExpiry) {
        delete this.cache[key];
      }
    });
    this.saveCache();
  }

  // Check if text should be skipped from translation
  private shouldSkipTranslation(text: string): boolean {
    const trimmedText = text.trim();

    // Skip single letters (like "M" in logos)
    if (trimmedText.length === 1 && /[A-Za-z]/.test(trimmedText)) {
      return true;
    }

    // Skip single symbols or punctuation
    if (trimmedText.length === 1 && /[^\w\s]/.test(trimmedText)) {
      return true;
    }

    // Skip very short text (1-2 characters)
    if (trimmedText.length <= 2) {
      return true;
    }

    // Skip text that's mostly numbers
    if (/^\d+$/.test(trimmedText)) {
      return true;
    }

    // Skip text that's mostly symbols
    if (/^[^\w\s]+$/.test(trimmedText)) {
      return true;
    }

    // Skip brand names and logos
    const brandNames = [
      "MealBridge",
      "SRI LANKA",
      "M", // Single M in logo
      "m", // Single m in logo
    ];

    if (brandNames.includes(trimmedText)) {
      return true;
    }

    return false;
  }

  // Translate text using AI
  async translateWithAI(text: string): Promise<{ si: string; ta: string }> {
    if (!this.isEnabled || !this.apiKey) {
      console.warn("AI translation not enabled or API key not set");
      return this.getFallbackTranslation(text);
    }

    // Skip translation for single letters, symbols, or brand elements
    if (this.shouldSkipTranslation(text)) {
      console.log("Skipping translation for:", text);
      return { si: text, ta: text };
    }

    // Check cache first
    const cached = this.getCachedTranslation(text);
    if (cached) {
      console.log("Using cached translation for:", text);
      return cached;
    }

    try {
      console.log("Translating with Gemini AI:", text);

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${this.apiKey}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: `You are an expert translator specializing in Sri Lankan languages with deep cultural understanding. Translate the given English text into natural, readable Sinhala and Tamil.

TRANSLATION GUIDELINES:

SINHALA TRANSLATION:
- Use proper Sinhala script (සිංහල)
- Write in natural, conversational Sinhala that sounds like a native speaker
- Use appropriate Sinhala sentence structure and word order
- Choose words that are commonly used in everyday Sinhala conversation
- Make it sound warm, friendly, and culturally appropriate
- Use honorifics (ඔබ, ඔබගේ) for formal contexts
- For food/charity context, use terms like "ආහාර", "පරිත්‍යාග", "සහයෝගය"

TAMIL TRANSLATION:
- Use proper Tamil script (தமிழ்)
- Write in natural, conversational Tamil that sounds like a native speaker
- Use appropriate Tamil sentence structure and word order
- Choose words that are commonly used in everyday Tamil conversation
- Make it sound warm, friendly, and culturally appropriate
- Use honorifics (நீங்கள், உங்கள்) for formal contexts
- For food/charity context, use terms like "உணவு", "தானம்", "உதவி"

CONTEXT: This is for MealBridge Sri Lanka - a food donation platform connecting surplus food with those in need.

EXAMPLES OF GOOD TRANSLATIONS:
- "Find food near you" → Sinhala: "ඔබ අසල ආහාර සොයා ගන්න" (natural, conversational)
- "Donate excess food" → Sinhala: "අතිරික්ත ආහාර පරිත්‍යාග කරන්න" (warm, inviting)
- "Join our community" → Sinhala: "අපගේ ප්‍රජාවට සම්බන්ධ වන්න" (welcoming)

CRITICAL: Respond ONLY with a valid JSON object in this exact format:
{"sinhala": "natural Sinhala translation", "tamil": "natural Tamil translation"}

Do not include any markdown formatting, code blocks, or additional text. Only the JSON object.

Translate this text: "${text}"`,
                  },
                ],
              },
            ],
            generationConfig: {
              temperature: 0.7, // Higher temperature for more natural, varied translations
              maxOutputTokens: 500,
            },
          }),
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          console.warn(
            "⚠️ Gemini API rate limit reached, using fallback translation"
          );
          return this.getFallbackTranslation(text);
        }
        throw new Error(`Gemini API error: ${response.status}`);
      }

      const data: GeminiResponse = await response.json();
      const content = data.candidates[0]?.content?.parts[0]?.text;

      if (!content) {
        throw new Error("No translation received from AI");
      }

      // Parse the JSON response - handle markdown code blocks
      let jsonContent = content.trim();

      // Remove markdown code blocks if present
      if (jsonContent.startsWith("```json")) {
        jsonContent = jsonContent
          .replace(/^```json\s*/, "")
          .replace(/\s*```$/, "");
      } else if (jsonContent.startsWith("```")) {
        jsonContent = jsonContent.replace(/^```\s*/, "").replace(/\s*```$/, "");
      }

      // Try to extract JSON from the response if it's not pure JSON
      const jsonMatch = jsonContent.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        jsonContent = jsonMatch[0];
      }

      console.log("🔍 Raw Gemini response:", content);
      console.log("🔍 Cleaned JSON content:", jsonContent);

      let translation;
      try {
        translation = JSON.parse(jsonContent);
      } catch (parseError) {
        console.error("❌ JSON parsing failed:", parseError);
        console.error("❌ Content that failed to parse:", jsonContent);
        const errorMessage =
          parseError instanceof Error ? parseError.message : String(parseError);
        throw new Error(`Failed to parse JSON response: ${errorMessage}`);
      }

      // Cache the translation
      this.cacheTranslation(text, translation.sinhala, translation.tamil);

      console.log("Gemini AI translation successful:", translation);
      return {
        si: translation.sinhala,
        ta: translation.tamil,
      };
    } catch (error) {
      console.error("Gemini AI translation failed:", error);
      return this.getFallbackTranslation(text);
    }
  }

  // Fallback translation using basic rules
  private getFallbackTranslation(text: string): { si: string; ta: string } {
    // Skip translation for brand names and logos
    if (this.shouldSkipTranslation(text)) {
      return { si: text, ta: text };
    }

    // Natural fallback translations for common phrases
    const fallbackTranslations: { [key: string]: { si: string; ta: string } } =
      {
        Home: { si: "මුල් පිටුව", ta: "முகப்பு" },
        "Find Food": { si: "ආහාර සොයා ගන්න", ta: "உணவைத் தேடுங்கள்" },
        Donate: { si: "පරිත්‍යාග කරන්න", ta: "தானம் செய்யுங்கள்" },
        Community: { si: "ප්‍රජාව", ta: "சமூகம்" },
        Impact: { si: "බලපෑම", ta: "தாக்கம்" },
        Login: { si: "පුරන්න", ta: "உள்நுழையுங்கள்" },
        "Sign Up": { si: "ලියාපදිංචි වන්න", ta: "பதிவு செய்யுங்கள்" },
        Welcome: { si: "ආයුබෝවන්", ta: "வரவேற்கிறோம்" },
        "Thank you": { si: "ස්තූතියි", ta: "நன்றி" },
        Please: { si: "කරුණාකර", ta: "தயவுசெய்து" },
        Yes: { si: "ඔව්", ta: "ஆம்" },
        No: { si: "නැහැ", ta: "இல்லை" },
        Cancel: { si: "අවලංගු කරන්න", ta: "ரத்து செய்" },
        Save: { si: "සුරකින්න", ta: "சேமி" },
        Delete: { si: "මකන්න", ta: "நீக்கு" },
        Edit: { si: "සංස්කරණය", ta: "திருத்து" },
        Submit: { si: "ඉදිරිපත් කරන්න", ta: "சமர்ப்பி" },
        Loading: { si: "පූරණය වෙමින්", ta: "ஏற்றுகிறது" },
        Error: { si: "දෝෂය", ta: "பிழை" },
        Success: { si: "සාර්ථකයි", ta: "வெற்றி" },
        "Bridge the Gap": { si: "පරතරය පියවන්න", ta: "இடைவெளியை நிரப்புங்கள்" },
        "Between Surplus & Need": {
          si: "අතිරික්තය සහ අවශ්‍යතාවය අතර",
          ta: "மிகுதி மற்றும் தேவைக்கு இடையே",
        },
        "Find food near you": {
          si: "ඔබ අසල ආහාර සොයා ගන්න",
          ta: "உங்கள் அருகில் உணவைத் தேடுங்கள்",
        },
        "Donate excess food": {
          si: "අතිරික්ත ආහාර පරිත්‍යාග කරන්න",
          ta: "மிகுதி உணவை தானம் செய்யுங்கள்",
        },
      };

    // Try exact match first
    if (fallbackTranslations[text]) {
      return fallbackTranslations[text];
    }

    // Try case-insensitive match
    const lowerText = text.toLowerCase();
    for (const [key, translation] of Object.entries(fallbackTranslations)) {
      if (key.toLowerCase() === lowerText) {
        return translation;
      }
    }

    // Return original text if no fallback found
    return { si: text, ta: text };
  }

  // Translate text for a specific language
  async translateText(
    text: string,
    language: "si" | "ta" | "en"
  ): Promise<string> {
    if (language === "en") {
      return text;
    }

    const translation = await this.translateWithAI(text);
    return translation[language];
  }

  // Batch translate multiple texts
  async translateBatch(
    texts: string[],
    language: "si" | "ta"
  ): Promise<string[]> {
    const translations = await Promise.all(
      texts.map((text) => this.translateText(text, language))
    );
    return translations;
  }

  // Check if AI translation is available
  isAITranslationAvailable(): boolean {
    return this.isEnabled;
  }

  // Get cache statistics
  getCacheStats(): { size: number; hitRate: number } {
    const totalEntries = Object.keys(this.cache).length;
    const validEntries = Object.values(this.cache).filter(
      (entry) => Date.now() - entry.timestamp < this.cacheExpiry
    ).length;

    return {
      size: totalEntries,
      hitRate: totalEntries > 0 ? validEntries / totalEntries : 0,
    };
  }

  // Clear cache
  clearCache() {
    this.cache = {};
    this.saveCache();
  }

  // Clean old cache entries
  cleanOldCache() {
    this.cleanCache();
  }
}
